import { Edge, Node } from '@xyflow/react';
import { useMemo } from 'react';

import type { SignalType } from '@features/create/CreateSignal';
import { NODE_GAP_PX, NODE_HEIGHT_PX } from '@features/create/constants';
import { Block, UpdateBlock } from '@features/create/types';
import { OpenSearchProps } from 'stores/pageStore';

const HORIZONTAL_GAP = 48;

const ROOT_LABELS: Record<SignalType, string> = {
  demand: 'Basket of needs',
};
const FIRST_CHILD_OFFSET = NODE_HEIGHT_PX + NODE_GAP_PX;
const VERTICAL_SPACING = NODE_HEIGHT_PX + NODE_GAP_PX;
const INITIAL_POSITION = { x: 16, y: 32 };
const EDGE_STYLE = { stroke: 'var(--chakra-colors-gray-300)' };
const BOTTOM_PADDING = 100;

export function useSimpleTreeLayout({
  blocks,
  addBlock,
  removeBlock,
  updateBlock,
  openSearch,
  signalType,
}: {
  blocks: Block[];
  addBlock: () => void;
  updateBlock: (id: string, update: UpdateBlock) => void;
  removeBlock: (blockId: string) => void;
  openSearch: (props: OpenSearchProps) => void;
  signalType: SignalType;
}): {
  nodes: Node[];
  edges: Edge[];
  maxHeight: number;
} {
  return useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const parentId = 'root';

    nodes.push({
      id: 'root',
      type: 'root',
      position: INITIAL_POSITION,
      data: { label: ROOT_LABELS[signalType] },
    });

    blocks.forEach((block, i) => {
      const childId = block.id;
      nodes.push({
        id: childId,
        type: 'commodity',
        position: {
          x: HORIZONTAL_GAP + INITIAL_POSITION.x,
          y: FIRST_CHILD_OFFSET + INITIAL_POSITION.y + i * VERTICAL_SPACING,
        },
        data: {
          block,
          onDelete: () => removeBlock(block.id),
          updateBlock,
          openSearch,
          signalType,
        },
      });
      edges.push({
        id: `e-${parentId}-${childId}`,
        source: parentId,
        target: childId,
        type: 'smoothstep',
        style: EDGE_STYLE,
      });
    });

    nodes.push({
      id: 'add-button',
      type: 'addButton',
      position: {
        x: HORIZONTAL_GAP + INITIAL_POSITION.x,
        y: FIRST_CHILD_OFFSET + INITIAL_POSITION.y + blocks.length * VERTICAL_SPACING,
      },
      data: {
        addBlock,
      },
    });
    edges.push({
      id: `e-${parentId}-add`,
      source: parentId,
      target: 'add-button',
      type: 'smoothstep',
      style: EDGE_STYLE,
    });

    const maxHeight =
      nodes.reduce((max, node) => Math.max(max, node.position.y), 0) +
      NODE_HEIGHT_PX +
      BOTTOM_PADDING;

    return { nodes, edges, maxHeight };
  }, [blocks, signalType, addBlock, updateBlock, removeBlock, openSearch]);
}
